import { useTranslation } from "react-i18next";
import {
  useCompanyApproveAssetRequestMutation,
  useCompanyGetSingleAssetTypeListQuery,
} from "@/services/(company)/api.assets";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { EnumAssetFileTypes } from "@/types/(company)/assets.interface";
import {
  ArrowLeft,
  Check,
  Loader2,
  MonitorSmartphone,
  PackageSearch,
  X,
} from "lucide-react";
import { cn } from "@/utils/cn";
import * as yup from "yup";
import { useStep } from "usehooks-ts";
import { useFormik } from "formik";
import { Textarea } from "@/components/ui/textarea";
import toast from "react-hot-toast";
import { ENUM_ASSET_STATUS } from "@/utils/enums";
import { startCase } from "lodash";

interface Props {
  requestId: number;
  oldAssetId?: string;
  assetTypeId: number;
  quantity?: number;
  children: ReactNode;
}

const FormSchema = yup.object({
  notes: yup.string().optional(),
  assetIds: yup.array().of(yup.number().required()).min(1).required(),
});
type Fields = yup.InferType<typeof FormSchema>;

/** ENUM_ASSET_STATUS values. Falls back to a humanized slug for anything else. */
const ASSET_STATUS_KEYS: Record<string, string> = {
  "in-use": "assets:in_use",
  maintenance: "assets:maintenance",
  active: "assets:active",
  retired: "assets:retired",
  lost: "assets:lost",
  available: "assets:available",
  damaged: "assets:damaged",
};
const assetStatusLabel = (t: (key: string) => string, status: string) =>
  ASSET_STATUS_KEYS[status] ? t(ASSET_STATUS_KEYS[status]) : startCase(status);

export const ActionAcceptAssetReqDialog = ({
  children,
  requestId,
  assetTypeId,
  oldAssetId,
  quantity = 1,
}: Props) => {
  const { t } = useTranslation(["assets", "common"]);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const [currentStep, steper] = useStep(2);
  const {
    canGoToPrevStep,
    canGoToNextStep,
    goToNextStep,
    goToPrevStep,
    reset: resetStep,
  } = steper;

  const [acceptRequest, { isLoading: isAssigningAsset }] =
    useCompanyApproveAssetRequestMutation();
  const { data: assets, isFetching: isFetchingAssets } =
    useCompanyGetSingleAssetTypeListQuery(
      { assetTypeId, filterOnlyAvailable: true },
      { skip: !isOpen },
    );

  const formik = useFormik<Fields>({
    validationSchema: FormSchema,
    initialValues: { notes: "", assetIds: [] },
    onSubmit: async ({ notes, assetIds }) => {
      const id = toast.loading(t("assets:processing"));
      await acceptRequest({
        requestId,
        notes,
        assetIds: assetIds as number[],
        oldAssetId: oldAssetId ? Number(oldAssetId) : undefined,
      })
        .unwrap()
        .then((res) => toast.success(res.message, { id }))
        .catch((e) =>
          toast.error(
            e?.data?.message ?? e?.data?.response?.message ?? e?.message,
            { id },
          ),
        );
    },
  });

  const handleClose = () => {
    formik.resetForm();
    resetStep();
    setIsOpen(false);
  };

  const selectedIds = formik.values.assetIds as number[];
  const selectedCount = selectedIds.length;
  const atLimit = selectedCount >= quantity;

  const toggleAsset = (id: number) => {
    if (selectedIds.includes(id)) {
      formik.setFieldValue(
        "assetIds",
        selectedIds.filter((a) => a !== id),
      );
    } else if (!atLimit) {
      formik.setFieldValue("assetIds", [...selectedIds, id]);
    }
  };

  const selectedAssets = assets?.data?.filter((a) =>
    selectedIds.includes(a.id),
  );
  const availableCount =
    assets?.data?.filter((a) => a.status === ENUM_ASSET_STATUS.AVAILABLE)
      .length ?? 0;

  return (
    <AlertDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
        else setIsOpen(true);
      }}
    >
      <AlertDialogTrigger
        asChild
        data-tooltip-id="hrm-tooltip"
        data-tooltip-content={t("assets:accept_this_asset_request")}
      >
        {children}
      </AlertDialogTrigger>

      <AlertDialogContent
        className="w-full md:max-w-4xl gap-0 p-0 overflow-hidden rounded-2xl shadow-2xl"
        asChild
      >
        <form
          onSubmit={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── HEADER ─────────────────────────────────────────── */}
          <div className="relative px-8 pt-7 pb-5 bg-white border-b border-slate-100">
            {/* Close button */}
            <button
              type="button"
              onClick={handleClose}
              aria-label={t("common:buttons.close")}
              className="absolute top-4 end-4 size-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X size={15} />
            </button>

            {/* Eyebrow + title */}
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.15em] uppercase text-primary bg-primary/8 px-2.5 py-1 rounded-full mb-3">
              <Check size={10} strokeWidth={3} />
              {t("assets:asset_approval")}
            </span>
            <h2 className="text-[1.25rem] font-bold text-slate-800 leading-tight">
              {currentStep === 1
                ? t("assets:choose_an_asset_to_assign")
                : t("assets:confirm_and_add_notes")}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {currentStep === 1
                ? t(
                    "assets:pick_an_available_asset_from_inventory_to_fulfill_this_r",
                  )
                : t(
                    "assets:review_your_selection_and_leave_an_optional_note_for_the",
                  )}
            </p>

            {/* ── STEPPER ── */}
            {/* Circles row — line must be at circle midpoint, so labels are separate */}
            <div className="flex items-center mt-5">
              <StepCircle step={1} current={currentStep} />
              {/* Progress line */}
              <div className="relative flex-1 mx-3 h-[2px] bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={cn(
                    "absolute inset-y-0 start-0 rounded-full bg-primary transition-all duration-500 ease-in-out",
                    currentStep > 1 ? "w-full" : "w-0",
                  )}
                />
              </div>
              <StepCircle step={2} current={currentStep} />
            </div>
            {/* Labels row — separate from circles so line stays aligned */}
            <div className="flex justify-between mt-1.5 px-[2px]">
              <span
                className={cn(
                  "text-[11px] font-semibold transition-colors",
                  currentStep >= 1 ? "text-primary" : "text-slate-400",
                )}
              >
                {t("assets:select_asset")}
              </span>
              <span
                className={cn(
                  "text-[11px] font-semibold transition-colors",
                  currentStep === 2 ? "text-primary" : "text-slate-400",
                )}
              >
                {t("assets:add_notes")}
              </span>
            </div>
          </div>

          {/* ── BODY ───────────────────────────────────────────── */}
          <div className="bg-slate-50/70">
            {currentStep === 1 ? (
              /* ── STEP 1 : Asset grid ── */
              <div className="px-8 py-5">
                {/* Toolbar */}
                {!isFetchingAssets && assets && assets.data.length > 0 && (
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs text-slate-500">
                      {t("assets:assets_available_select_up_to", {
                        count: availableCount,
                        max: quantity,
                      })}
                    </p>
                    {selectedCount > 0 && (
                      <button
                        type="button"
                        onClick={() => formik.setFieldValue("assetIds", [])}
                        className="text-[11px] text-slate-400 hover:text-danger underline underline-offset-2 transition-colors"
                      >
                        {t("assets:clear_selection")}
                      </button>
                    )}
                  </div>
                )}

                {/* Scrollable grid — padding-right to avoid scrollbar overlap */}
                <div className="max-h-108 min-h-88 overflow-y-auto overflow-x-hidden pe-1 -mr-1">
                  {isFetchingAssets ? (
                    <div className="flex flex-col items-center justify-center h-88 gap-3">
                      <div className="size-12 rounded-full bg-white border border-slate-200 shadow-xs flex items-center justify-center">
                        <Loader2 className="animate-spin size-5 text-primary" />
                      </div>
                      <p className="text-sm text-slate-400">
                        {t("assets:loading_assets")}
                      </p>
                    </div>
                  ) : assets && assets.data.length > 0 ? (
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pb-1">
                      {assets.data.map((asset) => {
                        const assetImage = asset.files?.find((f) =>
                          f.fileType.startsWith(EnumAssetFileTypes.assetImage),
                        );
                        const isSelected = selectedIds.includes(asset.id);
                        const isAvailable =
                          asset.status === ENUM_ASSET_STATUS.AVAILABLE;
                        const isSelectable =
                          isAvailable && (isSelected || !atLimit);

                        return (
                          <div
                            key={asset.id}
                            role={isSelectable ? "button" : undefined}
                            tabIndex={isSelectable ? 0 : undefined}
                            onClick={
                              isAvailable
                                ? () => toggleAsset(asset.id)
                                : undefined
                            }
                            onKeyDown={
                              isAvailable
                                ? (e) =>
                                    e.key === "Enter" && toggleAsset(asset.id)
                                : undefined
                            }
                            className={cn(
                              "group relative rounded-xl overflow-hidden bg-white border-2 transition-all duration-150 select-none outline-hidden",
                              isSelected
                                ? "border-primary ring-2 ring-primary/20 shadow-md"
                                : isSelectable
                                  ? "border-slate-200 hover:border-primary/60 hover:shadow-md cursor-pointer focus-visible:border-primary"
                                  : "border-slate-100 opacity-40 saturate-0 cursor-not-allowed pointer-events-none",
                            )}
                          >
                            {/* Image */}
                            <div className="relative w-full h-36 bg-slate-100 overflow-hidden">
                              {assetImage ? (
                                <img
                                  src={assetImage.fileUrl}
                                  className="size-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  alt={asset?.assetType?.name}
                                  draggable={false}
                                />
                              ) : (
                                <div className="size-full flex flex-col items-center justify-center gap-2 bg-linear-to-br from-slate-50 to-slate-100">
                                  <MonitorSmartphone
                                    size={32}
                                    className="text-slate-300"
                                  />
                                </div>
                              )}

                              {/* Hover hint overlay */}
                              {isSelectable && !isSelected && (
                                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/8 transition-colors duration-150 flex items-center justify-center">
                                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-[11px] font-semibold text-primary bg-white px-2.5 py-1 rounded-full shadow-xs">
                                    {t("common:buttons.select")}
                                  </span>
                                </div>
                              )}

                              {/* Selected overlay */}
                              {isSelected && (
                                <div className="absolute inset-0 bg-primary/70 flex items-center justify-center">
                                  <div className="size-11 rounded-full bg-white flex items-center justify-center shadow-lg">
                                    <Check
                                      size={20}
                                      className="text-primary"
                                      strokeWidth={3}
                                    />
                                  </div>
                                </div>
                              )}

                              {/* Status pill — only when not selected */}
                              {!isSelected && (
                                <span className="absolute top-2 start-2 bg-white/95 text-slate-600 text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-xs border border-slate-100/50">
                                  {assetStatusLabel(t, asset.status)}
                                </span>
                              )}
                            </div>

                            {/* Info */}
                            <div className="px-3 py-2.5 border-t border-slate-100">
                              <p className="text-sm font-semibold text-slate-800 truncate leading-snug">
                                {asset?.assetType?.name ??
                                  t("assets:unnamed_asset")}
                              </p>
                              <p className="text-[11px] text-slate-400 mt-0.5 font-mono tracking-tight">
                                {asset.assetTag}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-88 gap-4">
                      <div className="size-16 rounded-2xl bg-white border border-slate-200 shadow-xs flex items-center justify-center">
                        <PackageSearch size={28} className="text-slate-300" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-slate-600">
                          {t("assets:no_assets_available")}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {t(
                            "assets:all_assets_of_this_type_are_currently_in_use",
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* ── STEP 2 : Confirm + Notes ── */
              <div className="px-8 py-6 grid md:grid-cols-[1fr_1fr] gap-6">
                {/* Left — selected assets list */}
                <div className="flex flex-col gap-2">
                  <p className="text-[10px] font-bold tracking-[0.12em] uppercase text-slate-400">
                    {t("assets:assigning_count", { count: selectedCount })}
                  </p>
                  <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto pe-1">
                    {selectedAssets?.map((asset) => {
                      const img = asset.files?.find((f) =>
                        f.fileType.startsWith(EnumAssetFileTypes.assetImage),
                      );
                      return (
                        <div
                          key={asset.id}
                          className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white shadow-xs px-3 py-2.5"
                        >
                          <div className="size-10 shrink-0 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
                            {img ? (
                              <img
                                src={img.fileUrl}
                                className="size-full object-cover"
                                alt=""
                                draggable={false}
                              />
                            ) : (
                              <MonitorSmartphone
                                size={18}
                                className="text-slate-300"
                              />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate leading-snug">
                              {asset.assetType?.name ?? t("assets:asset")}
                            </p>
                            <p className="text-[11px] text-slate-400 font-mono">
                              {asset.assetTag}
                            </p>
                          </div>
                          <div className="ms-auto shrink-0 flex items-center gap-1 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                            <Check size={9} strokeWidth={3} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right — notes */}
                <div className="flex flex-col gap-2">
                  <div>
                    <p className="text-[10px] font-bold tracking-[0.12em] uppercase text-slate-400">
                      {t("assets:notes_for_employee")}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {t("assets:optional_the_employee_will_see_this_message")}
                    </p>
                  </div>
                  <Textarea
                    className="h-[148px] resize-none bg-white text-sm border-slate-200 placeholder:text-slate-300"
                    value={formik.values.notes}
                    onChange={formik.handleChange}
                    name={"notes" satisfies keyof Fields}
                    placeholder={t(
                      "assets:e_g_please_handle_this_device_with_care_and_return_it_wh",
                    )}
                  />
                  <p className="text-[11px] text-slate-400">
                    {t("assets:leaving_this_blank_is_perfectly_fine")}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── FOOTER ─────────────────────────────────────────── */}
          <div className="flex items-center justify-between px-8 py-4 bg-white border-t border-slate-100">
            <Button
              type="button"
              variant="outline"
              onClick={canGoToPrevStep ? goToPrevStep : handleClose}
              disabled={isFetchingAssets}
              className="gap-1.5 text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
            >
              {canGoToPrevStep ? (
                <>
                  <ArrowLeft size={14} className="rtl:rotate-180" />
                  {t("common:buttons.back")}
                </>
              ) : (
                t("common:buttons.cancel")
              )}
            </Button>

            <div className="flex items-center gap-3">
              {currentStep === 1 && selectedCount > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium text-primary bg-primary/8 px-2.5 py-1 rounded-full">
                  <Check size={10} strokeWidth={3} />
                  {t("assets:n_of_m_selected", {
                    selected: selectedCount,
                    total: quantity,
                  })}
                </span>
              )}
              <Button
                type="button"
                onClick={canGoToNextStep ? goToNextStep : formik.submitForm}
                disabled={
                  isFetchingAssets || isAssigningAsset || selectedCount === 0
                }
                className="min-w-[140px] gap-2 bg-primary text-white disabled:opacity-40"
              >
                {isAssigningAsset ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />{" "}
                    {t("assets:assigning_ellipsis")}
                  </>
                ) : canGoToNextStep ? (
                  <>
                    {t("common:buttons.continue")} <ArrowRight />
                  </>
                ) : (
                  <>
                    <Check size={14} strokeWidth={3} />
                    {t("assets:assign_asset")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/* ── Helpers ────────────────────────────────────────────────── */

const ArrowRight = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/** Only the circle — labels are rendered separately so the connecting line stays at circle midpoint */
const StepCircle = ({ step, current }: { step: number; current: number }) => {
  const done = current > step;
  const active = current === step;
  return (
    <div
      className={cn(
        "size-8 shrink-0 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300",
        done
          ? "bg-primary border-primary text-white"
          : active
            ? "bg-white border-primary text-primary shadow-xs"
            : "bg-white border-slate-200 text-slate-400",
      )}
    >
      {done ? <Check size={13} strokeWidth={3} /> : step}
    </div>
  );
};
